// Bandeja de Salida — cola de documentos del abogado que tiene que subir al PJ.
// Agrupado por expediente. Permite descargar uno a uno, todo el grupo de un exp
// en ZIP, o todo el ZIP completo. Quitar de la cola es soft delete (no elimina
// el documento del sistema, solo lo saca de la cola del abogado).

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
    Box, Paper, Typography, IconButton, Tooltip, Chip,
    Button, Stack, Skeleton, Alert, Collapse,
    Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import GavelIcon from '@mui/icons-material/Gavel';
import DescriptionIcon from '@mui/icons-material/Description';
import ArticleIcon from '@mui/icons-material/Article';
import AssignmentIcon from '@mui/icons-material/Assignment';
import OutboxIcon from '@mui/icons-material/Outbox';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { saveAs } from 'file-saver';
import {
    bandejaSalidaListarJWT, bandejaSalidaQuitarJWT,
    bandejaSalidaDescargarBlob, bandejaSalidaDescargarZipBlob,
} from '../../api/endpoints';

const PRIMARY = '#1769aa';
const BR = 4;

const TIPO_INFO = {
    CARTA_PODER:        { label: 'Carta Poder',           color: '#1769aa', icon: <DescriptionIcon fontSize="small" /> },
    TOMA_PARTICIPACION: { label: 'Toma de Participación', color: '#2e7d32', icon: <ArticleIcon fontSize="small" /> },
    DEMANDA:            { label: 'Demanda',               color: '#6a1b9a', icon: <GavelIcon fontSize="small" /> },
    ESCRITO:            { label: 'Escrito',               color: '#546e7a', icon: <AssignmentIcon fontSize="small" /> },
};
const tipoInfo = (t) => TIPO_INFO[t] || { label: t, color: '#777', icon: <ArticleIcon fontSize="small" /> };

function fmtFecha(s) {
    if (!s) return '';
    try {
        return new Date(s).toLocaleString('es-AR', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit', hour12: false,
        });
    } catch { return ''; }
}

export default function BandejaSalidaHome() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandidos, setExpandidos] = useState(new Set());
    const [confirmQuitar, setConfirmQuitar] = useState(null);
    const [descargandoZip, setDescargandoZip] = useState(false);

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            const r = await bandejaSalidaListarJWT();
            if (r?.ok) setItems(r.items || []);
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { cargar(); }, [cargar]);

    // Agrupar por expediente.
    const grupos = useMemo(() => {
        const map = new Map();
        for (const it of items) {
            const key = it.Id_Expediente;
            if (!map.has(key)) {
                map.set(key, {
                    idExpediente: it.Id_Expediente,
                    caratula: it.Caratula,
                    autos: it.Autos,
                    area: it.Area,
                    docs: [],
                });
            }
            map.get(key).docs.push(it);
        }
        return Array.from(map.values());
    }, [items]);

    // Auto-expandir todos al inicio (es poca data y la idea es que vean qué tienen)
    useEffect(() => {
        if (grupos.length > 0 && expandidos.size === 0) {
            setExpandidos(new Set(grupos.map(g => g.idExpediente)));
        }
        // eslint-disable-next-line
    }, [grupos.length]);

    const toggleExpandido = (id) => {
        setExpandidos(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const descargarUno = async (doc) => {
        try {
            const blob = await bandejaSalidaDescargarBlob(doc.Id);
            const ext = doc.Tiene_PDF ? 'pdf' : 'html';
            const nombre = `${doc.Titulo}.${ext}`.replace(/[^a-zA-Z0-9_\-\s.()]/g, '_');
            saveAs(blob, nombre);
            // Recargar para reflejar "descargado"
            setTimeout(cargar, 500);
        } catch (e) {
            console.error(e);
        }
    };

    const descargarZipGrupo = async (idExpediente) => {
        try {
            setDescargandoZip(true);
            const blob = await bandejaSalidaDescargarZipBlob([idExpediente]);
            saveAs(blob, `bandeja-exp${idExpediente}-${new Date().toISOString().slice(0, 10)}.zip`);
            setTimeout(cargar, 500);
        } finally { setDescargandoZip(false); }
    };

    const descargarZipTodo = async () => {
        try {
            setDescargandoZip(true);
            const blob = await bandejaSalidaDescargarZipBlob();
            saveAs(blob, `bandeja-salida-${new Date().toISOString().slice(0, 10)}.zip`);
            setTimeout(cargar, 500);
        } finally { setDescargandoZip(false); }
    };

    const quitar = async () => {
        if (!confirmQuitar) return;
        await bandejaSalidaQuitarJWT(confirmQuitar.Id);
        setConfirmQuitar(null);
        await cargar();
    };

    const totalDocs = items.length;
    const totalDescargados = items.filter(it => it.Descargado).length;
    const pendientes = totalDocs - totalDescargados;

    return (
        <Box sx={{ p: 2.5, bgcolor: '#f6f8fc', minHeight: '100vh' }}>
            {/* Header */}
            <Paper sx={{
                p: 2.5, borderRadius: `${BR}px`, mb: 2,
                background: `linear-gradient(135deg, ${PRIMARY} 0%, #2196f3 100%)`, color: 'white',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <OutboxIcon sx={{ fontSize: 40 }} />
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>Bandeja de Salida</Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            Documentos pendientes de subir al Poder Judicial
                        </Typography>
                    </Box>
                </Box>
                <Stack direction="row" spacing={1}>
                    <Tooltip title="Refrescar"><IconButton onClick={cargar}
                        sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                        <RefreshIcon />
                    </IconButton></Tooltip>
                </Stack>
            </Paper>

            {/* Resumen */}
            <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                <Chip label={`${totalDocs} documento${totalDocs !== 1 ? 's' : ''}`} sx={{ fontWeight: 600 }} />
                <Chip label={`${pendientes} pendiente${pendientes !== 1 ? 's' : ''}`} color="warning" sx={{ fontWeight: 600 }} />
                <Chip label={`${totalDescargados} descargado${totalDescargados !== 1 ? 's' : ''}`}
                    color="success" variant="outlined" sx={{ fontWeight: 600 }} />
                <Chip label={`${grupos.length} expediente${grupos.length !== 1 ? 's' : ''}`} variant="outlined" />
                <Box sx={{ flex: 1 }} />
                {totalDocs > 0 && (
                    <Button variant="contained" startIcon={<FolderZipIcon />}
                        onClick={descargarZipTodo} disabled={descargandoZip}
                        sx={{ textTransform: 'none', fontWeight: 600, bgcolor: PRIMARY }}>
                        Descargar todo (ZIP)
                    </Button>
                )}
            </Stack>

            {/* Lista */}
            {loading ? (
                <Box>
                    {[1, 2, 3].map(i => <Skeleton key={i} variant="rounded" height={80} sx={{ mb: 1, borderRadius: `${BR}px` }} />)}
                </Box>
            ) : grupos.length === 0 ? (
                <Alert severity="info" sx={{ borderRadius: `${BR}px` }}>
                    Tu bandeja está vacía. Los documentos se agregan automáticamente al firmarse cartas poder
                    y demandas, o desde el botón "Enviar a cola" en los escritos del expediente.
                </Alert>
            ) : grupos.map(g => {
                const expandido = expandidos.has(g.idExpediente);
                return (
                    <Paper key={g.idExpediente} sx={{ mb: 1.5, borderRadius: `${BR}px`, overflow: 'hidden' }}>
                        <Box sx={{
                            p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer',
                            bgcolor: '#f0f4f8',
                            borderBottom: expandido ? '1px solid #e0e0e0' : 'none',
                            '&:hover': { bgcolor: '#e3eaf2' },
                        }} onClick={() => toggleExpandido(g.idExpediente)}>
                            <IconButton size="small">
                                {expandido ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: PRIMARY }}>
                                        {g.autos || `Exp ${g.idExpediente}`}
                                    </Typography>
                                    {g.area && <Chip size="small" label={g.area} sx={{ height: 18, fontSize: '0.7rem' }} />}
                                    <Chip size="small" label={`${g.docs.length} doc${g.docs.length !== 1 ? 's' : ''}`}
                                        color="primary" sx={{ height: 18, fontSize: '0.7rem' }} />
                                </Stack>
                                <Typography variant="caption" sx={{ color: '#555', display: 'block', mt: 0.3 }}>
                                    {g.caratula}
                                </Typography>
                            </Box>
                            <Tooltip title="Descargar todos los del expediente (ZIP)">
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); descargarZipGrupo(g.idExpediente); }}
                                    disabled={descargandoZip}>
                                    <FolderZipIcon sx={{ color: PRIMARY }} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Abrir expediente en nueva ventana">
                                <IconButton size="small" onClick={(e) => { e.stopPropagation();
                                    window.open(`/expediente/judicial/${g.idExpediente}`, '_blank', 'noopener,noreferrer');
                                }}>
                                    <OpenInNewIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Tooltip>
                        </Box>

                        <Collapse in={expandido} timeout={250}>
                            <Box sx={{ p: 1.2 }}>
                                {g.docs.map(doc => {
                                    const info = tipoInfo(doc.Tipo);
                                    return (
                                        <Box key={doc.Id} sx={{
                                            display: 'flex', alignItems: 'center', gap: 1, py: 0.8,
                                            px: 1.2, borderRadius: `${BR}px`,
                                            bgcolor: doc.Descargado ? 'transparent' : '#fffdf0',
                                            borderLeft: `3px solid ${info.color}`,
                                            mb: 0.5,
                                            '&:hover': { bgcolor: '#f5f5f5' },
                                        }}>
                                            <Box sx={{ color: info.color }}>{info.icon}</Box>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Chip size="small" label={info.label}
                                                        sx={{ bgcolor: info.color + '22', color: info.color, fontWeight: 600, height: 18, fontSize: '0.7rem' }} />
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{doc.Titulo}</Typography>
                                                    {doc.Descargado === 1 && (
                                                        <Tooltip title={`Descargado el ${fmtFecha(doc.Fecha_Descarga)}`}>
                                                            <CheckCircleIcon sx={{ fontSize: 14, color: '#2e7d32' }} />
                                                        </Tooltip>
                                                    )}
                                                </Stack>
                                                <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>
                                                    Encolado: {fmtFecha(doc.Fecha_Encolado)}
                                                    {doc.Origen_Info && ` · ${doc.Origen_Info}`}
                                                </Typography>
                                            </Box>
                                            <Tooltip title="Descargar">
                                                <IconButton size="small" onClick={() => descargarUno(doc)}>
                                                    <DownloadIcon fontSize="small" sx={{ color: PRIMARY }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Quitar de la cola">
                                                <IconButton size="small" color="error"
                                                    onClick={() => setConfirmQuitar(doc)}>
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Collapse>
                    </Paper>
                );
            })}

            <Dialog open={!!confirmQuitar} onClose={() => setConfirmQuitar(null)} maxWidth="xs" fullWidth
                PaperProps={{ sx: { borderRadius: '4px' } }}>
                <DialogTitle>¿Quitar de la cola?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Vas a quitar <strong>{confirmQuitar?.Titulo}</strong> de tu bandeja de salida.
                        El documento sigue existiendo en el expediente — solo se saca de esta cola.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmQuitar(null)}>Cancelar</Button>
                    <Button color="error" variant="contained" onClick={quitar}>Quitar</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
